// src/screens/Marketplace/EditStoreScreen.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './EditStoreScreen.css';
import { compressImage } from '../../utils/compressImage';
import { uploadToSpaces } from '../../utils/uploadToSpaces';
import api from '../../api';

// ✅ ISO codes list (centralized)
import { ISO_COUNTRY_CODES } from '../../constants/countryCodes';

const sanitizeLocationInput = (value) =>
  value.replace(/[^\p{L}\p{N}\s,-]/gu, '').trim();

function EditStoreScreen({ user }) {
  const [store, setStore] = useState(null);

  // Old single-string field kept for payload compatibility
  const [location, setLocation] = useState('');

  // New structured selection
  const [countryCode, setCountryCode] = useState('');     // e.g. "ZA" or "OTHER"
  const [countryName, setCountryName] = useState('');     // e.g. "South Africa"
  const [customCountry, setCustomCountry] = useState(''); // free text for OTHER

  const [description, setDescription] = useState('');
  const [bannerImage, setBannerImage] = useState('');
  const [businessLogo, setBusinessLogo] = useState('');
  const [businessUsername, setBusinessUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Build alphabetical country list once
  const countries = useMemo(() => {
    const dn = new Intl.DisplayNames(['en'], { type: 'region' });
    const list = ISO_COUNTRY_CODES
      .map(code => ({ code, name: dn.of(code) || code }))
      .filter(c => c.name && c.name.length > 1)
      .sort((a, b) => a.name.localeCompare(b.name));

    return [...list, { code: 'OTHER', name: 'Other' }];
  }, []);

  // Helper: try to map an existing string to a code by name match
  const inferCodeFromName = (name) => {
    if (!name) return '';
    const lower = String(name).toLowerCase().trim();
    const found = countries.find(c => c.code !== 'OTHER' && c.name.toLowerCase() === lower);
    return found?.code || '';
  };

  // On mount, fetch store and prefill fields
  useEffect(() => {
    const fetchStore = async () => {
      try {
        const res = await api.get(`/stores/by-user/${user.username}`);
        const s = res.data;
        setStore(s);
        setDescription(s.description || '');
        setLocation(s.location || '');
        setBannerImage(s.bannerImage || '');
        setBusinessLogo(s.businessLogo || '');
        setBusinessUsername(s.businessUsername || '');

        // Prefill dropdown based on existing location string
        const guessedCode = inferCodeFromName(s.location);
        if (guessedCode) {
          setCountryCode(guessedCode);
          const found = countries.find(c => c.code === guessedCode);
          setCountryName(found?.name || s.location || '');
          setCustomCountry('');
        } else if (s.location) {
          // unknown/legacy free text → OTHER
          setCountryCode('OTHER');
          setCountryName('');
          setCustomCountry(s.location);
        } else {
          // nothing set yet
          setCountryCode('');
          setCountryName('');
          setCustomCountry('');
        }
      } catch (err) {
        console.error('❌ Failed to fetch store:', err);
      }
    };
    fetchStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.username, countries]);

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const compressed = await compressImage(file, 0.8);
    const url = await uploadToSpaces(compressed, 'merchant-banners');
    setBannerImage(url);
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const compressed = await compressImage(file);
    const url = await uploadToSpaces(compressed, 'business-logos');
    setBusinessLogo(url);
  };

  // Decide final string for payload
  const resolveLocationString = () => {
    if (countryCode === 'OTHER') {
      return sanitizeLocationInput(customCountry);
    }
    return countryName || '';
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const updatedData = {
        // Keep sending a single `location` string (no backend change needed)
        location: resolveLocationString(),
        description,
        bannerImage,
        businessLogo,
      };

      await api.put(`/stores/update/${store._id}`, updatedData);
      navigate(`/store/${businessUsername}`, { replace: true });
    } catch (err) {
      console.error('❌ Failed to update store:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="editstore-fullscreen">
      <div className="editstore-header">Edit Store</div>

      <div className="editstore-scroll-area">
        <div className="editstore-content">
          {/* Country dropdown replacing free text */}
          <label>Store Country</label>
          <select
            value={countryCode}
            onChange={(e) => {
              const code = e.target.value;
              setCountryCode(code);
              if (code === 'OTHER') {
                setCountryName('');
                setCustomCountry('');
                setLocation(''); // keep old text field in sync (not shown)
              } else {
                const found = countries.find(c => c.code === code);
                const name = found?.name || '';
                setCountryName(name);
                setCustomCountry('');
                setLocation(name); // keep old field in sync (legacy)
              }
            }}
            required
          >
            <option value="" disabled>Select your country</option>
            {countries.map(({ code, name }) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>

          {/* If Other, show manual input */}
          {countryCode === 'OTHER' && (
            <>
              <label>Your Country (type it)</label>
              <input
                type="text"
                value={customCountry}
                onChange={(e) => setCustomCountry(sanitizeLocationInput(e.target.value))}
                placeholder="Type your country"
              />
            </>
          )}

          <label>Store Description</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={6}
          />

          <label>Update Banner</label>
          <input type="file" accept="image/*" onChange={handleBannerUpload} />
          {bannerImage && (
            <img
              src={bannerImage}
              className="editstore-preview"
              alt="Banner preview"
            />
          )}

          <label>Update Logo</label>
          <input type="file" accept="image/*" onChange={handleLogoUpload} />
          {businessLogo && (
            <img
              src={businessLogo}
              className="editstore-preview logo"
              alt="Logo preview"
            />
          )}
        </div>
      </div>

      <div className="editstore-footer">
        <button onClick={() => navigate(`/store/${businessUsername}`, { replace: true })}>
          Cancel
        </button>
        <button onClick={handleSubmit} disabled={loading}>
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}

export default EditStoreScreen;
